import {
  ECommonTraitNodeType,
  IBaseTraitNode,
  IOptionalChosenTraitNode,
  type IBaseChosenTraitNode
} from '../types/talentNodeTypes';
import { TIERED_APEX_NODE_FLAGS, hasSomeTraitNodeFlags } from './traitNodeFlags';
import { canonicalEntryIndexForBuild } from './preferenceChoiceNodes';

// talent boundaries. Calculations are: >= minX < maxX

// const TALENTS_BOTTOM_1_ROW_MIN_Y = 6600;
// const TALENTS_BOTTOM_3_ROWS_MIN_Y = 5090;

// for dividing the posX into left/right sides
const CENTER_POINT_X = 8000;

export function sortTalentsByVisualNodeOrder<T extends IOptionalChosenTraitNode>(a: T, b: T) {
  const aHorizontalSide = a.node.posX < CENTER_POINT_X;
  const bHorizontalSide = b.node.posX < CENTER_POINT_X;

  // we want nodes that fall on left side of tree first
  if (aHorizontalSide === bHorizontalSide) {
    if (Math.abs(a.node.posY - b.node.posY) < 10) {
      // same vertical but sort left side nodes first
      return a.node.posX - b.node.posX;
    }

    // higher nodes go first, but there is some slop in exact positions
    // from blizzard's talent system so we just ignore some precision
    return a.node.posY - b.node.posY;
  } else if (aHorizontalSide) {
    return -1;
  } else {
    return 1;
  }
}

export function joinChosenWithSpecNodes(
  allNodes: IBaseTraitNode[],
  chosen: IBaseChosenTraitNode[]
): IOptionalChosenTraitNode[] {
  const allNodeIds = new Set(allNodes.map((n) => n.id));
  const merged = allNodes.map<IOptionalChosenTraitNode>((node) => {
    const entry = chosen.find((n) => n.node.id === node.id);
    return entry ? { ...entry, node: { ...entry.node, ...node } } : { node };
  });
  for (const entry of chosen) {
    if (!allNodeIds.has(entry.node.id)) {
      merged.push(entry);
    }
  }
  return merged;
}

export type TUpdatedTalent<T> = {
  talent: T;
  prevTalent: T;
  rankDiff: number;
};

export type TTalentNodeCompareResult<T extends IBaseChosenTraitNode | IOptionalChosenTraitNode = IBaseChosenTraitNode> =
  {
    added: T[];
    removed: T[];
    updated: Array<TTalentNodeCompareUpdatedEntry<T>>;
  };

export type TTalentNodeCompareUpdatedEntry<T extends IBaseChosenTraitNode | IOptionalChosenTraitNode> = {
  talent: T;
  prevTalent: T;
  rankDiff: number;
};

export const compareTalentsBuild = <T extends IBaseChosenTraitNode | IOptionalChosenTraitNode>(
  currentTalents: T[],
  previousTalents: T[]
): TTalentNodeCompareResult<T> => {
  const filteredCurrentTalents = filterSpentTalentNodes(currentTalents);
  const filteredPreviousTalents = filterSpentTalentNodes(previousTalents);
  const added: T[] = [];
  const removed: T[] = [];
  const updated: TUpdatedTalent<T>[] = [];

  for (const talent of filteredCurrentTalents) {
    const talentEntry = canonicalEntryIndexForBuild(talent.node, talent.entryIndex);
    const prev = filteredPreviousTalents.find(
      (t) => t.node.id === talent.node.id && canonicalEntryIndexForBuild(t.node, t.entryIndex) === talentEntry
    );
    if (!prev) {
      added.push(talent);
    } else if (prev.rank !== talent.rank) {
      updated.push({ talent, prevTalent: prev, rankDiff: (talent.rank ?? 0) - (prev.rank ?? 0) });
    }
  }

  for (const talent of filteredPreviousTalents) {
    const talentEntry = canonicalEntryIndexForBuild(talent.node, talent.entryIndex);
    if (
      !filteredCurrentTalents.some(
        (t) => t.node.id === talent.node.id && canonicalEntryIndexForBuild(t.node, t.entryIndex) === talentEntry
      )
    ) {
      removed.push(talent);
    }
  }

  return { added, removed, updated };
};

export const getTalentRankDiff = <T extends IBaseChosenTraitNode | IOptionalChosenTraitNode>(
  talent: T,
  compareTalent: T | undefined
): number | undefined => {
  if (
    !compareTalent ||
    canonicalEntryIndexForBuild(talent.node, talent.entryIndex) !==
      canonicalEntryIndexForBuild(compareTalent.node, compareTalent.entryIndex)
  ) {
    return undefined;
  }
  if (talent.rank == null || compareTalent.rank == null) {
    return undefined;
  }
  if (talent.rank === compareTalent.rank) {
    return undefined;
  }
  return talent.rank - compareTalent.rank;
};

export const isActiveTalentNode = <T extends IOptionalChosenTraitNode>(talent: T | undefined | null): boolean => {
  if (!talent) {
    return false;
  }

  return typeof talent.entryIndex === 'number' || talent.grantedNode === true;
};

export const isSpentTalentNode = <T extends IOptionalChosenTraitNode>(talent: T | undefined | null): boolean => {
  return isActiveTalentNode(talent) && talent?.grantedNode !== true;
};

export const filterActiveTalentNodes = <T extends IOptionalChosenTraitNode>(talents: T[]): T[] => {
  return talents.filter(isActiveTalentNode);
};

export const filterSpentTalentNodes = <T extends IOptionalChosenTraitNode>(talents: T[]): T[] => {
  return talents.filter(isSpentTalentNode);
};

export const getTalentsHash = <T extends IBaseChosenTraitNode>(talents: T[]) => {
  return filterSpentTalentNodes(talents)
    .concat()
    .sort((a, b) => a.node.id - b.node.id)
    .map((talent) => `${talent.node.id}-${canonicalEntryIndexForBuild(talent.node, talent.entryIndex)}`)
    .join(';');
};

// Rank-aware canonical signature identifying a single specific build, with preference choice nodes
// collapsed. Unlike getTalentsHash (which groups builds and ignores rank), this keeps partial-rank
// variants distinct, so only true preference-only twins (e.g. an Earthquake targeting variant)
// produce an identical signature and get coalesced into one build.
export const getCanonicalBuildSignature = <T extends IBaseChosenTraitNode>(talents: T[]) => {
  return filterSpentTalentNodes(talents)
    .concat()
    .sort((a, b) => a.node.id - b.node.id)
    .map(
      (talent) => `${talent.node.id}-${canonicalEntryIndexForBuild(talent.node, talent.entryIndex)}-${talent.rank ?? 0}`
    )
    .join(';');
};

export const specTalentsHash = (specId: number, talents: IBaseChosenTraitNode[]) => {
  const { specTalents, heroTalents } = categorizeSpentLoadoutTalents(specId, talents, { showAll: true });
  const specHash = getTalentsHash(specTalents);
  const heroHash = getTalentsHash(heroTalents);

  return {
    specHash,
    heroHash,
    specHeroHash: `${specHash}|${heroHash}`
  };
};

export const MID_ROW_IMPORTANCE_WEIGHTS_BY_SPEC: any = {
  65: 0 // Holy Paladin
};
export const TALENTS_BOUNDARIES = {
  classTalents: {
    minX: 0,
    maxX: 7000
  },
  specTalents: {
    minX: 9000,
    maxX: 16384
  },
  heroTalents: {
    minX: 7000,
    maxX: 9000
  }
};

// Retail nodes normalize subTreeId to numeric 0/non-zero values, while
// classic/era shapes may leave it undefined. The truthy check preserves the
// existing "hero nodes are in a subtree, class/spec nodes are not" split.
export function isSpecTalentNode<T extends IBaseTraitNode>(node: T): boolean {
  if (node.type === ECommonTraitNodeType.SubTreeSelection) {
    return false;
  }
  if (node.subTreeId) {
    return false;
  }
  return node.posX >= TALENTS_BOUNDARIES.specTalents.minX;
}

function computeIncludeInSummary<T extends IOptionalChosenTraitNode>(specId: number, talent: T): boolean {
  if (talent.node.important || (talent.node.subTreeId && talent.node.type === ECommonTraitNodeType.Selection)) {
    return true;
  }

  let importance = 0;

  if (talent.node.type === ECommonTraitNodeType.Selection) {
    importance++;
  }

  const slotHasActiveAbility = talent.node.entries.some((entry) => entry.spell?.hasCooldown);
  if (slotHasActiveAbility) {
    importance++;
  }

  const midRowImportanceWeight =
    typeof MID_ROW_IMPORTANCE_WEIGHTS_BY_SPEC[specId] !== 'undefined' ? MID_ROW_IMPORTANCE_WEIGHTS_BY_SPEC[specId] : 2;
  if (talent.node.row === 0) {
    importance += 2;
  } else if (talent.node.row <= midRowImportanceWeight) {
    importance++;
  }

  return importance >= 2;
}

type TCategorizeTalentsConfig = {
  showAll?: boolean;
  heroSubTreeId?: number;
  skipOnIncorrectHeroSubTreeId?: boolean;
};

type TCategorizedTalents<T extends IOptionalChosenTraitNode> = {
  classTalents: T[];
  specTalents: T[];
  heroTalents: T[];
  heroSubTreeId: number;
};

export function categorizeTalentsByTree<T extends IOptionalChosenTraitNode>(
  specId: number,
  loadout: T[],
  config: TCategorizeTalentsConfig = {}
): TCategorizedTalents<T> {
  const { showAll = false, heroSubTreeId: heroSubTreeIdParam, skipOnIncorrectHeroSubTreeId } = config;
  const classTalents: T[] = [];
  const specTalents: T[] = [];
  const heroTalents: T[] = [];
  let heroSubTreeId: number = heroSubTreeIdParam ?? 0;

  for (const talent of loadout) {
    if (talent.node.type === ECommonTraitNodeType.SubTreeSelection) {
      continue;
    }

    const includeInSummary = computeIncludeInSummary(specId, talent);

    if (!showAll && !includeInSummary) {
      continue;
    }

    const talentToPush = includeInSummary ? { ...talent, includeInSummary: true } : talent;

    if (talent.node.subTreeId) {
      if (heroSubTreeId === 0) {
        heroSubTreeId = talent.node.subTreeId;
      }

      if (heroSubTreeId !== talent.node.subTreeId) {
        if (skipOnIncorrectHeroSubTreeId) {
          continue;
        }
        console.error(`Expected latched hero sub tree ${heroSubTreeId} to match ${talent.node.subTreeId}`);
      }

      heroTalents.push(talentToPush as T);
    } else if (isSpecTalentNode(talent.node)) {
      specTalents.push(talentToPush as T);
    } else {
      classTalents.push(talentToPush as T);
    }
  }

  classTalents.sort(sortTalentsByVisualNodeOrder);
  specTalents.sort(sortTalentsByVisualNodeOrder);
  heroTalents.sort(sortTalentsByVisualNodeOrder);

  if (config.heroSubTreeId === undefined && heroTalents.length > 0) {
    heroSubTreeId = heroTalents[0].node.subTreeId!;
  }

  return { classTalents, specTalents, heroTalents, heroSubTreeId };
}

export function categorizeSpentLoadoutTalents<T extends IOptionalChosenTraitNode>(
  specId: number,
  loadout: T[],
  config: TCategorizeTalentsConfig = {}
): TCategorizedTalents<T> {
  return categorizeTalentsByTree(specId, filterSpentTalentNodes(loadout), config);
}

export const isTieredApexTalent = (talent: IOptionalChosenTraitNode): boolean => {
  return (
    talent.node.type === ECommonTraitNodeType.Tiered && hasSomeTraitNodeFlags(talent.node.flags, TIERED_APEX_NODE_FLAGS)
  );
};
export enum TalentScope {
  Class = 'class',
  Spec = 'spec',
  Hero = 'hero'
}

export function categorizeTalentsByScopes(
  specId: number,
  chosenNodes: IBaseChosenTraitNode[],
  scopes: TalentScope[] = [TalentScope.Class, TalentScope.Spec, TalentScope.Hero]
): Record<TalentScope, IBaseChosenTraitNode[]> {
  const categorized = categorizeSpentLoadoutTalents(specId, chosenNodes, { showAll: true });

  const result: Record<TalentScope, IBaseChosenTraitNode[]> = {
    [TalentScope.Class]: [],
    [TalentScope.Spec]: [],
    [TalentScope.Hero]: []
  };

  const map: Record<TalentScope, keyof typeof categorized> = {
    [TalentScope.Class]: 'classTalents',
    [TalentScope.Spec]: 'specTalents',
    [TalentScope.Hero]: 'heroTalents'
  };

  for (const scope of scopes) {
    // @ts-ignore
    result[scope] = categorized[map[scope]];
  }

  return result;
}

export const isChoiceNode = (node: IBaseTraitNode): boolean => {
  return node.entries.length >= 2;
};
